import { modal, router, setPageContent } from "..";
import {
	putSmartplug,
	resetSmartplugConsumption,
	unregisterSmartplug,
} from "../api/smartplugs_{imei}";
import { PriceWrapper, prices } from "../billing/prices";
import {
	nameField,
	ownerField,
	sharingField,
	subscriptionField,
	tagsField,
} from "../device/device_settings_fields";
import { openUnregisterDeviceModal } from "../device/remove_device_modal";
import { errorSection } from "../error-section";
import { loadingScreen } from "../loading-screen";
import { getWeekdays, kitchenTimeString, t } from "../locale";
import { removeNotificationListener } from "../mobile";
import { state } from "../state";
import { Bulma } from "../view/bulma";
import { centeredBox } from "../view/components";
import { MaterialDesign } from "../view/material_design";

/**
 * @type {import('navigo').Handler}
 */
async function smartplugSettingsPage(ctx) {
	if (!ctx?.data?.imei) throw "missing ctx.data.imei";
	const imei = ctx.data.imei;
	const sp = state.smartplugs[ctx.data.imei];

	const user = await state.getActiveUser();
	const isOwner = user.Email === sp.Metadata.OwnerEmail;

	const { SmartplugMonth, SmartplugYear } = await prices;

	const beep = soundSignalField(sp);
	const powerFailureAlarm = powerFailureAlarmField(sp);
	const loadAlarm = loadAlarmField(sp);
	const temperatureAlarm = temperatureAlarmField(sp);
	const timer = timerField(sp);
	const thermostat = thermostatField(sp);
	const subscription = subscriptionField(
		sp.Subscription,
		[
			new PriceWrapper(user.Country),
			new PriceWrapper(user.Country, SmartplugMonth),
			new PriceWrapper(user.Country, SmartplugYear),
		],
		isOwner,
	);
	const sharing = sharingField(sp.Metadata);
	const tags = tagsField(sp.Metadata);
	const name = nameField(sp.Metadata.Name);
	const owner = ownerField(sp.Metadata, isOwner);

	setPageContent(
		centeredBox(
			"small",
			Bulma.title(`SmartPlug - ${sp.Metadata.Name}`, "is-4"),
			Bulma.subtitle(`IMEI: ${sp.Imei}`, "is-6"),
			consumptionField(ctx, sp),
			beep.element,
			powerFailureAlarm.element,
			loadAlarm.element,
			temperatureAlarm.element,
			timer.element,
			thermostat.element,
			subscription.element,
			sharing.element,
			tags.element,
			name.element,
			owner.element,
			document.createElement("hr"),
			Bulma.buttons(
				Bulma.button(
					{
						onClick: onClickSave,
						styles: ["is-primary"],
					},
					t.save,
				),
				Bulma.button(
					{
						onClick: () => router.jump(`/devices?id=${sp.Imei}`),
						styles: ["is-primary"],
					},
					t.back,
				),
				Bulma.button(
					{
						disabled: !isOwner,
						onClick: () => openUnregisterDeviceModal(unregister),
						styles: ["is-danger"],
					},
					t.remove,
				),
			),
		),
	);

	async function onClickSave() {
		const attempt = errorSection.attempt(async () => {
			const sp = await putSmartplug(imei, {
				BeepEnabled: beep.isOn(),
				PowerFailureAlarmEnabled: powerFailureAlarm.isOn(),
				LoadAlarm: {
					Enabled: loadAlarm.isOn(),
					Lower: loadAlarm.getLower(),
					Upper: loadAlarm.getUpper(),
				},
				TemperatureRangeAlarm: {
					Enabled: temperatureAlarm.isOn(),
					Lower: temperatureAlarm.getLower(),
					Upper: temperatureAlarm.getUpper(),
				},
				Timer: {
					Enabled: timer.isOn(),
					Off: timer.getOffSchedules(),
					On: timer.getOnSchedules(),
				},
				Thermostat: {
					Enabled: thermostat.isOn(),
					Triggers: thermostat.getTriggers(),
				},
				PriceId: subscription.getPriceId(),
				Access: sharing.getAccess(),
				Notify: sharing.getNotify(),
				Tags: tags.getTags(),
				Name: name.getName(),
				Owner: owner.getOwner(),
			});
			state.smartplugs[sp.Imei] = sp;
			router.jump(`/devices?id=${sp.Imei}`);
		});
		loadingScreen.waitFor(attempt);
	}

	function unregister() {
		const attempt = errorSection.attempt(async () => {
			await unregisterSmartplug(sp.Imei);
			delete state.smartplugs[sp.Imei];
			router.jump("/devices");
		});
		loadingScreen.waitFor(attempt);
	}
}

/**
 * @param {Smartplug} sp
 */
function soundSignalField(sp) {
	const select = Bulma.select({
		options: [
			Bulma.option(t.on, "on", sp.BeepEnabled),
			Bulma.option(t.off, "off", !sp.BeepEnabled),
		],
	});

	const field = Bulma.field(
		{},
		Bulma.label(t.smartplug.settingsPage.deviceBeepLabel),
		Bulma.control({}, select.element),
	);

	return {
		element: field,
		isOn: () => select.getValue() === "on",
	};
}

/**
 * @param {Smartplug} sp
 */
function powerFailureAlarmField(sp) {
	const isSubscribed =
		sp.Subscription.PriceIds.Current !== "" ||
		Date.now() < new Date(sp.Subscription.ExpiresAt).getTime();

	const select = Bulma.select({
		disabled: !isSubscribed,
		options: [
			Bulma.option(
				t.on,
				"on",
				isSubscribed && sp.PowerFailureAlarm.Enabled,
			),
			Bulma.option(
				t.off,
				"off",
				!isSubscribed || !sp.PowerFailureAlarm.Enabled,
			),
		],
	});

	const field = Bulma.field(
		{},
		Bulma.label(t.smartplug.settingsPage.powerFailureNotificationsLabel),
		Bulma.control({}, select.element),
	);

	return {
		element: field,
		isOn: () => select.getValue() === "on",
	};
}

/**
 * @param {import('navigo').Match} ctx
 * @param {Smartplug} sp
 */
function consumptionField(ctx, sp) {
	const field = Bulma.field(
		{},
		Bulma.label(
			`${t.smartplug.settingsPage.consumptionLabel} ${sp.Consumption} Wh`,
		),
		Bulma.control(
			{},
			Bulma.button(
				{
					onClick: onClickReset,
					styles: ["is-primary"],
				},
				t.reset,
			),
		),
	);

	async function onClickReset() {
		const imei = sp.Imei;
		const attempt = errorSection.attempt(async () => {
			const sp = await resetSmartplugConsumption(imei);
			state.smartplugs[imei] = sp;
			removeNotificationListener("smartplug", imei);
			smartplugSettingsPage(ctx);
		});
		loadingScreen.waitFor(attempt);
	}

	return field;
}

/**
 * @param {Smartplug} sp
 */
function loadAlarmField(sp) {
	const isSubscribed =
		sp.Subscription.PriceIds.Current !== "" ||
		Date.now() < new Date(sp.Subscription.ExpiresAt).getTime();

	const onOff = Bulma.select({
		disabled: !isSubscribed,
		options: [
			Bulma.option(t.on, "on", isSubscribed && sp.LoadAlarm.Enabled),
			Bulma.option(t.off, "off", !isSubscribed || !sp.LoadAlarm.Enabled),
		],
	});

	const lower = Bulma.input({
		type: "text",
		onInput: () => {
			const rawLower = lower.getValue();
			if (!rawLower) return;

			const sanitizedLower = rawLower.replace(/(?!^)-|[^-\d]/g, "");
			if (sanitizedLower.length < 2) {
				lower.setValue(sanitizedLower);
				return;
			}

			const parsedLower = +sanitizedLower;
			if (parsedLower < 0) {
				lower.setValue("0");
				return;
			}

			const rawUpper = upper.getValue();
			const parsedUpper = rawUpper ? +rawUpper : 3700;
			if (parsedLower >= parsedUpper) {
				lower.setValue((parsedUpper - 1).toString());
				return;
			}

			lower.setValue(parsedLower.toString());
		},
	});

	const upper = Bulma.input({
		type: "text",
		onInput: () => {
			const rawUpper = upper.getValue();
			if (!rawUpper) return;

			const sanitizedUpper = rawUpper.replace(/(?!^)-|[^-\d]/g, "");
			if (sanitizedUpper.length < 2) {
				upper.setValue(sanitizedUpper);
				return;
			}

			const parsedUpper = +sanitizedUpper;
			if (parsedUpper > 3700) {
				upper.setValue("3700");
				return;
			}

			const rawLower = lower.getValue();
			const parsedLower = rawLower ? +rawLower : 0;
			if (parsedUpper <= parsedLower) {
				upper.setValue((parsedLower + 1).toString());
				return;
			}

			upper.setValue(parsedUpper.toString());
		},
	});

	const openEditModal = () => {
		modal.open({
			header: t.smartplug.settingsPage.powerLoadNotificationsLabel,
			body: [
				Bulma.field(
					{},
					Bulma.label(
						t.smartplug.settingsPage.lowestExpectedLoadLabel,
					),
					Bulma.control({}, lower.element),
				),
				Bulma.field(
					{},
					Bulma.label(
						t.smartplug.settingsPage.highestExpectedLoadLabel,
					),
					Bulma.control({}, upper.element),
				),
			],
			footer: [
				Bulma.button(
					{
						onClick: () => modal.close(),
						styles: ["is-primary"],
					},
					t.done,
				),
			],
		});
	};

	const field = Bulma.field(
		{},
		Bulma.label(t.smartplug.settingsPage.powerLoadNotificationsLabel),
		Bulma.field(
			{
				styles: ["is-grouped"],
			},
			Bulma.control({}, onOff.element),
			Bulma.control(
				{},
				Bulma.button(
					{
						onClick: openEditModal,
						styles: ["is-primary"],
					},
					t.edit,
				),
			),
			Bulma.control(
				{},
				Bulma.input({
					type: "text",
					value: `${sp.LoadAlarm.Lower} - ${sp.LoadAlarm.Upper} W`,
					styles: ["is-static"],
				}).element,
			),
		),
	);

	return {
		element: field,
		isOn: () => onOff.getValue() === "on",
		getLower: () => +lower.getValue(),
		getUpper: () => +upper.getValue(),
	};
}

/**
 * @param {Smartplug} sp
 */
function temperatureAlarmField(sp) {
	const isSubscribed =
		sp.Subscription.PriceIds.Current !== "" ||
		Date.now() < new Date(sp.Subscription.ExpiresAt).getTime();

	const onOff = Bulma.select({
		disabled: !isSubscribed,
		options: [
			Bulma.option(
				t.on,
				"on",
				isSubscribed && sp.TemperatureRangeAlarm.Enabled,
			),
			Bulma.option(
				t.off,
				"off",
				!isSubscribed || !sp.TemperatureRangeAlarm.Enabled,
			),
		],
	});

	const lower = Bulma.input({
		type: "text",
		onInput: () => {
			const rawLower = lower.getValue();
			if (!rawLower) return;

			const sanitizedLower = rawLower.replace(/(?!^)-|[^-\d]/g, "");
			if (sanitizedLower.length < 2) {
				lower.setValue(sanitizedLower);
				return;
			}

			const parsedLower = +sanitizedLower;
			if (parsedLower < -20) {
				lower.setValue("-20");
				return;
			}

			const rawUpper = upper.getValue();
			const parsedUpper = rawUpper ? +rawUpper : 70;
			if (parsedLower >= parsedUpper) {
				lower.setValue((parsedUpper - 1).toString());
				return;
			}

			lower.setValue(parsedLower.toString());
		},
	});

	const upper = Bulma.input({
		type: "text",
		onInput: () => {
			const rawUpper = upper.getValue();
			if (!rawUpper) return;

			const sanitizedUpper = rawUpper.replace(/(?!^)-|[^-\d]/g, "");
			if (sanitizedUpper.length < 2) {
				upper.setValue(sanitizedUpper);
				return;
			}

			const parsedUpper = +sanitizedUpper;
			if (parsedUpper > 70) {
				upper.setValue("70");
				return;
			}

			const rawLower = lower.getValue();
			const parsedLower = rawLower ? +rawLower : -20;
			if (parsedUpper <= parsedLower) {
				upper.setValue((parsedLower + 1).toString());
				return;
			}

			upper.setValue(parsedUpper.toString());
		},
	});

	const openEditModal = () => {
		modal.open({
			header: t.smartplug.settingsPage.temperatureNotifications,
			body: [
				Bulma.field(
					{},
					Bulma.control({}, lower.element),
					Bulma.help(
						t.smartplug.settingsPage.lowestExpectedTempLabel,
					),
				),
				Bulma.field(
					{},
					Bulma.control({}, upper.element),
					Bulma.help(
						t.smartplug.settingsPage.highestExpectedTempLabel,
					),
				),
			],
			footer: [
				Bulma.button(
					{
						onClick: () => modal.close(),
						styles: ["is-primary"],
					},
					t.done,
				),
			],
		});
	};

	const field = Bulma.field(
		{},
		Bulma.label(t.smartplug.settingsPage.temperatureNotifications),
		Bulma.field(
			{
				styles: ["is-grouped"],
			},
			Bulma.control({}, onOff.element),
			Bulma.control(
				{},
				Bulma.button(
					{
						onClick: openEditModal,
						styles: ["is-primary"],
					},
					t.edit,
				),
			),
			Bulma.control(
				{},
				Bulma.input({
					type: "text",
					value: `${sp.TemperatureRangeAlarm.Lower} - ${sp.TemperatureRangeAlarm.Upper} °C`,
					styles: ["is-static"],
				}).element,
			),
		),
	);

	return {
		element: field,
		isOn: () => onOff.getValue() === "on",
		getLower: () => +lower.getValue(),
		getUpper: () => +upper.getValue(),
	};
}

/**
 * @param {Smartplug} sp
 */
function timerField(sp) {
	const isSubscribed =
		sp.Subscription.PriceIds.Current !== "" ||
		Date.now() < new Date(sp.Subscription.ExpiresAt).getTime();

	const onOff = Bulma.select({
		disabled: !isSubscribed,
		options: [
			Bulma.option(t.on, "on", isSubscribed && sp.Timer.Enabled),
			Bulma.option(t.off, "off", !isSubscribed || !sp.Timer.Enabled),
		],
	});

	const action = Bulma.select({
		options: [Bulma.option(t.on, "on"), Bulma.option(t.off, "off")],
	});

	const time = Bulma.timeInput();

	const days = getWeekdays().map((_day, i, week) =>
		Bulma.checkbox({
			text: week[(i + 1) % 7].substring(0, 2),
		}),
	);

	const addRule = () => {
		const [hour, minute] = time.getUtc().split(":");
		const dayIndices = days
			.map((checkbox, i) => (checkbox.isChecked() ? (i + 1) % 7 : -1))
			.filter((i) => i >= 0);

		const a = action.getValue();
		if (a !== "on" && a !== "off") throw `invalid action: ${action}`;

		table.insertRow(new TimerRuleRow(table, +hour, +minute, dayIndices, a));
		modal.close();
	};

	const openNewRuleModal = () => {
		modal.open({
			header: t.smartplug.settingsPage.timer.newRuleLabel,
			body: [
				Bulma.field(
					{
						styles: ["is-grouped"],
					},
					Bulma.field(
						{},
						Bulma.label(
							t.smartplug.settingsPage.automation.actionLabel,
						),
						Bulma.control({}, action.element),
					),
					Bulma.field(
						{},
						Bulma.label(t.time),
						Bulma.control({}, time.element),
					),
				),
				Bulma.field(
					{},
					Bulma.label(t.days),
					Bulma.control(
						{},
						Bulma.checkboxes(...days.map((day) => day.element)),
					),
				),
			],
			footer: [
				Bulma.button(
					{
						onClick: addRule,
						styles: ["is-primary"],
					},
					t.add,
				),
				Bulma.button(
					{
						onClick: () => modal.close(),
						styles: ["is-danger"],
					},
					t.cancel,
				),
			],
		});
	};

	const table = new TimerRulesTable(sp);

	const field = Bulma.field(
		{},
		Bulma.label(t.smartplug.settingsPage.timer.label),
		Bulma.field(
			{
				styles: ["is-grouped"],
			},
			Bulma.control({}, onOff.element),
			Bulma.control(
				{},
				Bulma.button(
					{
						onClick: openNewRuleModal,
						styles: ["is-primary"],
					},
					t.add,
				),
			),
		),
		table.element,
	);

	return {
		element: field,
		getOffSchedules: () => table.offSchedules,
		getOnSchedules: () => table.onSchedules,
		isOn: () => onOff.getValue() === "on",
	};
}

/**
 * @param {Smartplug} sp
 */
function thermostatField(sp) {
	const isSubscribed =
		sp.Subscription.PriceIds.Current !== "" ||
		Date.now() < new Date(sp.Subscription.ExpiresAt).getTime();

	const onOff = Bulma.select({
		disabled: !isSubscribed,
		options: [
			Bulma.option(t.on, "on", isSubscribed && sp.Thermostat.Enabled),
			Bulma.option(t.off, "off", !isSubscribed || !sp.Thermostat.Enabled),
		],
	});

	const action = Bulma.select({
		options: [
			Bulma.option(t.smartplug.settingsPage.automation.turnOn, "on"),
			Bulma.option(t.smartplug.settingsPage.automation.turnOff, "off"),
		],
	});

	const direction = Bulma.select({
		options: [
			Bulma.option(t.smartplug.settingsPage.thermostat.above, "above"),
			Bulma.option(t.smartplug.settingsPage.thermostat.below, "below"),
		],
	});

	const temperature = Bulma.input({
		type: "text",
	});

	const days = getWeekdays().map((_day, i, week) =>
		Bulma.checkbox({
			text: week[(i + 1) % 7].substring(0, 2),
		}),
	);

	const addRule = () => {
		const d = direction.getValue();
		if (d !== "above" && d !== "below")
			throw `invalid thermostat rule direction: ${d}`;

		const a = action.getValue();
		if (a !== "on" && a !== "off") throw `invalid action: ${action}`;

		const dayIndices = days
			.map((checkbox, i) => (checkbox.isChecked() ? (i + 1) % 7 : -1))
			.filter((i) => i >= 0);

		table.insertRow(
			new ThermostatRuleRow(
				table,
				d,
				+temperature.getValue(),
				a,
				dayIndices,
			),
		);
		modal.close();
	};

	const openNewRuleModal = () => {
		modal.open({
			header: t.smartplug.settingsPage.thermostat.newRuleLabel,
			body: [
				Bulma.field(
					{},
					Bulma.label(t.smartplug.settingsPage.automationActionLabel),
					Bulma.control({}, action.element),
				),
				Bulma.label(
					t.smartplug.settingsPage.thermostat.temperatureLabel,
				),
				Bulma.field(
					{
						styles: ["is-grouped"],
					},
					Bulma.control({}, direction.element),
					Bulma.control({}, temperature.element),
				),
				Bulma.field(
					{},
					Bulma.label(t.days),
					Bulma.control(
						{},
						Bulma.checkboxes(...days.map((day) => day.element)),
					),
				),
			],
			footer: [
				Bulma.button(
					{
						onClick: addRule,
						styles: ["is-primary"],
					},
					t.add,
				),
				Bulma.button(
					{
						onClick: () => modal.close(),
						styles: ["is-danger"],
					},
					t.cancel,
				),
			],
		});
	};

	const table = new ThermostatRulesTable(sp);

	const field = Bulma.field(
		{},
		Bulma.label(t.smartplug.settingsPage.thermostat.label),
		Bulma.field(
			{
				styles: ["is-grouped"],
			},
			Bulma.control({}, onOff.element),
			Bulma.control(
				{},
				Bulma.button(
					{
						onClick: openNewRuleModal,
						styles: ["is-primary"],
					},
					t.add,
				),
			),
		),
		table.element,
	);

	return {
		element: field,
		getTriggers: () => table.triggers,
		isOn: () => onOff.getValue() === "on",
	};
}

class TimerRulesTable {
	/**
	 * @type {TimerRuleRow[]}
	 */
	rows = [];

	/**
	 * @param {Smartplug} sp
	 */
	constructor(sp) {
		if (!sp.Timer.On) sp.Timer.On = [];
		sp.Timer.On.forEach((cron) => {
			this.rows.push(TimerRuleRow.fromCron(this, cron, "on"));
		});

		if (!sp.Timer.Off) sp.Timer.Off = [];
		sp.Timer.Off.forEach((cron) => {
			this.rows.push(TimerRuleRow.fromCron(this, cron, "off"));
		});

		this.rows.sort(TimerRuleRow.ascending);

		this.element = Bulma.table({
			classList: ["is-fullwidth"],
			body: this.rows.map((row) => row.element),
		});
	}

	get offSchedules() {
		return this.rows
			.filter((row) => row.action === "off")
			.map((row) => row.schedule);
	}

	get onSchedules() {
		return this.rows
			.filter((row) => row.action === "on")
			.map((row) => row.schedule);
	}

	/**
	 * @param {TimerRuleRow} row
	 */
	insertRow(row) {
		// calculate index
		let i = 0;
		while (this.rows[i] && TimerRuleRow.ascending(this.rows[i], row) < 1)
			i++;

		// update model
		this.rows.splice(i, 0, row);

		// update view
		this.element.insertRow(i).replaceWith(row.element);
	}

	/**
	 * @param {TimerRuleRow} row
	 */
	removeRow(row) {
		const i = this.rows.indexOf(row);
		this.rows.splice(i, 1);
		row.element.remove();
	}
}

class TimerRuleRow {
	/**
	 * @param {TimerRulesTable} parent
	 * @param {number} hour
	 * @param {number} minute
	 * @param {number[]} days
	 * @param {"on"|"off"} action
	 */
	constructor(parent, hour, minute, days, action) {
		this.hour = hour;
		this.minute = (Math.ceil(minute / 5) * 5) % 60;
		this.days = days;
		this.action = action;

		const deleteIcon = MaterialDesign.icon({
			id: "close",
			onClick: () => parent.removeRow(this),
		});

		const date = new Date();
		date.setUTCHours(this.hour);
		date.setUTCMinutes(this.minute);

		const time = kitchenTimeString(date);

		this.element = Bulma.tableRow(
			{},
			Bulma.tableDataCell(time),
			Bulma.tableDataCell(
				`${days.map((day) => getWeekdays()[(day + 1) % 7].substring(0, 2)).join(" ")}`,
			),
			Bulma.tableDataCell(action),
			Bulma.tableDataCell(deleteIcon),
		);
	}

	get schedule() {
		return `${this.minute} ${this.hour} ${this.days.join(",")}`;
	}

	/**
	 * @param {TimerRuleRow} a
	 * @param {TimerRuleRow} b
	 * @returns {number}
	 */
	static ascending(a, b) {
		if (a.hour !== b.hour) return a.hour - b.hour;
		if (a.minute !== b.minute) return a.minute - b.minute;
		return a.action === "on" ? -1 : 1;
	}

	/**
	 * @param {TimerRulesTable} table
	 * @param {string} cron // minute hour days
	 * @param {"on"|"off"} action
	 * @returns {TimerRuleRow}
	 */
	static fromCron(table, cron, action) {
		const [minute, hour, days] = cron.split(" ");
		return new TimerRuleRow(
			table,
			+hour,
			+minute,
			days.split(",").map(Number),
			action,
		);
	}
}

class ThermostatRulesTable {
	/**
	 * @type {ThermostatRuleRow[]}
	 */
	rows = [];

	/**
	 * @param {Smartplug} sp
	 */
	constructor(sp) {
		if (!sp.Thermostat.Triggers) sp.Thermostat.Triggers = [];
		sp.Thermostat.Triggers.forEach((trigger) => {
			this.rows.push(
				new ThermostatRuleRow(
					this,
					trigger.Above ? "above" : "below",
					trigger.Temperature,
					trigger.SetPowerEnabled ? "on" : "off",
					trigger.Days,
				),
			);
		});

		this.rows.sort(ThermostatRuleRow.ascending);

		this.element = Bulma.table({
			classList: ["is-fullwidth"],
			body: this.rows.map((row) => row.element),
		});
	}

	get triggers() {
		return this.rows.map((row) => row.toTrigger());
	}

	/**
	 * @param {ThermostatRuleRow} row
	 */
	insertRow(row) {
		// calculate index
		let i = 0;
		while (
			this.rows[i] &&
			ThermostatRuleRow.ascending(this.rows[i], row) < 1
		)
			i++;

		// update model
		this.rows.splice(i, 0, row);

		// update view
		this.element.insertRow(i).replaceWith(row.element);
	}

	/**
	 * @param {ThermostatRuleRow} row
	 */
	removeRow(row) {
		const i = this.rows.indexOf(row);
		this.rows.splice(i, 1);
		row.element.remove();
	}
}

class ThermostatRuleRow {
	/**
	 * @param {ThermostatRulesTable} parent
	 * @param {"above"|"below"} direction
	 * @param {number} temperature
	 * @param {"on"|"off"} action
	 * @param {number[]} days
	 */
	constructor(parent, direction, temperature, action, days) {
		this.direction = direction;
		this.temperature = temperature;
		this.action = action;
		this.days = days;

		const deleteIcon = MaterialDesign.icon({
			id: "close",
			onClick: () => parent.removeRow(this),
		});

		this.element = Bulma.tableRow(
			{},
			Bulma.tableDataCell(direction),
			Bulma.tableDataCell(temperature.toString()),
			Bulma.tableDataCell(action),
			Bulma.tableDataCell(
				`${days.map((day) => getWeekdays()[(day + 1) % 7].substring(0, 2)).join(" ")}`,
			),
			Bulma.tableDataCell(deleteIcon),
		);
	}

	toTrigger() {
		return {
			Days: this.days,
			Above: this.direction === "above",
			Temperature: this.temperature,
			SetPowerEnabled: this.action === "on",
		};
	}

	/**
	 * @param {ThermostatRuleRow} a
	 * @param {ThermostatRuleRow} b
	 * @returns {number}
	 */
	static ascending(a, b) {
		return a.temperature - b.temperature;
	}
}

export { smartplugSettingsPage };
